import type { FieldError, FieldErrors, Path } from "react-hook-form";

/**
 * Returns the first human-readable validation message from react-hook-form errors.
 */
export function getFirstFormErrorMessage<T extends Record<string, any>>(
  errors: FieldErrors<T>,
): string | undefined {
  for (const value of Object.values(errors)) {
    if (!value) continue;

    const fieldError = value as FieldError | FieldErrors<T>;
    if (
      typeof fieldError === "object" &&
      "message" in fieldError &&
      fieldError.message
    ) {
      return String(fieldError.message);
    }

    if (typeof fieldError === "object") {
      const nested = getFirstFormErrorMessage(
        fieldError as FieldErrors<Record<string, any>>,
      );
      if (nested) return nested;
    }
  }

  return undefined;
}

/**
 * Focuses the first invalid field in a form when validation fails.
 * This should be used as the onError callback for react-hook-form's handleSubmit.
 * 
 * @example
 * ```tsx
 * const { handleSubmit, formState: { errors } } = useForm<FormData>();
 * 
 * <form onSubmit={handleSubmit(onSubmit, (errors) => focusFirstInvalidField(errors))}>
 *   ...
 * </form>
 * ```
 */
export function focusFirstInvalidField<T extends Record<string, any>>(
  errors: FieldErrors<T>
): void {
  // Get the first error field name
  const firstErrorField = Object.keys(errors)[0] as Path<T>;
  
  if (!firstErrorField) {
    return;
  }

  // Try to find the input element by name
  // React Hook Form typically sets the name attribute on inputs
  const fieldName = String(firstErrorField);
  
  // Try multiple strategies to find the input
  let inputElement: HTMLElement | null = null;

  // Strategy 1: Find by name attribute (most common)
  inputElement = document.querySelector(`input[name="${fieldName}"], textarea[name="${fieldName}"], select[name="${fieldName}"]`) as HTMLElement;

  // Strategy 2: Find by id (if FormLabel has htmlFor)
  if (!inputElement) {
    inputElement = document.getElementById(fieldName) as HTMLElement;
  }

  // Strategy 3: Find by data-name attribute (if components use it)
  if (!inputElement) {
    inputElement = document.querySelector(`[data-name="${fieldName}"]`) as HTMLElement;
  }

  // Strategy 4: For nested fields (e.g., "students.0.firstName"), try to find by partial match
  if (!inputElement && fieldName.includes('.')) {
    const parts = fieldName.split('.');
    const lastPart = parts[parts.length - 1];
    // Try to find by the last part of the nested path
    inputElement = document.querySelector(`input[name*="${lastPart}"], textarea[name*="${lastPart}"], select[name*="${lastPart}"]`) as HTMLElement;
  }

  // Strategy 5: Find the first invalid FormControl and focus its input
  if (!inputElement) {
    // Find all FormControls with isInvalid (Chakra UI sets aria-invalid="true")
    const formControls = document.querySelectorAll('[aria-invalid="true"]');
    if (formControls.length > 0) {
      const firstInvalidControl = formControls[0];
      // Try to find input/textarea/select within the FormControl
      inputElement = firstInvalidControl.querySelector('input, textarea, select, button[role="button"]') as HTMLElement;
    }
  }

  // Strategy 6: Find by aria-describedby (Chakra UI links error messages to inputs)
  if (!inputElement) {
    const errorMessages = document.querySelectorAll('[role="alert"]');
    if (errorMessages.length > 0) {
      const firstError = errorMessages[0];
      const describedBy = firstError.getAttribute('id');
      if (describedBy) {
        inputElement = document.querySelector(`[aria-describedby*="${describedBy}"]`) as HTMLElement;
      }
    }
  }

  // Focus the element if found
  if (inputElement) {
    // Use setTimeout to ensure the DOM has updated
    setTimeout(() => {
      inputElement?.focus();
      // Scroll into view with smooth behavior
      inputElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }
}

